import * as React from 'react'
import { todayInSaoPaulo } from '@supply/contracts'
import { CalendarDays, ChevronLeft, ChevronRight, Clock, type LucideIcon } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { maskTimeInput, parseTimeInput, roundUpToQuarter, stepTime, type Parsed } from '@/lib/typed-values'
import { cn } from '@/lib/utils'

/*
 * Seletores de data, mês e hora do sistema (substituem os <input type="date|month|time"> nativos).
 * Valor sempre "YYYY-MM-DD" ('' = vazio); mês em "YYYY-MM"; hora em "HH:MM".
 * Desktop (≥ sm): popover ancorado no campo. Mobile: folha inferior com alvos de 44px.
 * Teclado: setas movem o dia, PageUp/PageDown o mês, Home/End a semana, Enter seleciona.
 * A hora também aceita digitação direta com máscara (ver `TypedField`); o seletor fica no ícone.
 * Ao escolher um dia, `thenFocus` leva o foco direto para a hora.
 */

const MONTHS = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro']
const MONTHS_SHORT = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']
const WEEKDAYS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S']
const WEEKDAYS_FULL = ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado']

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/
const ISO_MONTH = /^(\d{4})-(\d{2})$/

function utc(y: number, m: number, d: number): Date {
  return new Date(Date.UTC(y, m, d))
}

function iso(date: Date): string {
  return date.toISOString().slice(0, 10)
}

function parts(value: string): [number, number, number] {
  const [y, m, d] = value.split('-').map(Number) as [number, number, number]
  return [y, m - 1, d]
}

function addDays(value: string, n: number): string {
  const [y, m, d] = parts(value)
  return iso(utc(y, m, d + n))
}

function addMonths(value: string, n: number): string {
  const [y, m, d] = parts(value)
  const last = utc(y, m + n + 1, 0).getUTCDate()
  return iso(utc(y, m + n, Math.min(d, last)))
}

/** "2026-10-21" → "21/10/2026". */
export function formatDateValue(value: string): string {
  const match = ISO_DATE.exec(value)
  return match ? `${match[3]}/${match[2]}/${match[1]}` : value
}

const DESKTOP_QUERY = '(min-width: 640px)'

function useDesktop(): boolean {
  return React.useSyncExternalStore(
    (onChange) => {
      const media = window.matchMedia(DESKTOP_QUERY)
      media.addEventListener('change', onChange)
      return () => media.removeEventListener('change', onChange)
    },
    () => window.matchMedia(DESKTOP_QUERY).matches,
    () => true,
  )
}

/** Popover no desktop, folha inferior no mobile — mesmo conteúdo. */
function PickerShell({
  open,
  onOpenChange,
  title,
  trigger,
  manualFocus = false,
  renderField,
  returnFocus,
  children,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  trigger: React.ReactElement
  /** Ao abrir, foca o dia do cursor (célula com tabIndex 0) em vez do primeiro botão. */
  manualFocus?: boolean
  /** Campo digitável: recebe o gatilho (ícone) e devolve o campo inteiro, onde o popover se ancora. */
  renderField?: (trigger: React.ReactNode) => React.ReactElement
  /** Ao fechar, devolve o foco para cá (o campo de texto) em vez do gatilho. */
  returnFocus?: React.RefObject<HTMLElement | null>
  children: React.ReactNode
}) {
  const desktop = useDesktop()
  // Precisa ser aqui: dentro de Sheet/Dialog, focar antes do FocusScope montar faz o pai roubar o foco.
  const keepFocus = manualFocus
    ? (e: Event) => {
        const target = (e.currentTarget as HTMLElement).querySelector<HTMLElement>('[data-picker-focus]')
        if (!target) return
        e.preventDefault()
        target.focus({ preventScroll: true })
      }
    : undefined
  const backToField = returnFocus
    ? (e: Event) => {
        if (!returnFocus.current) return
        e.preventDefault()
        returnFocus.current.focus({ preventScroll: true })
      }
    : undefined

  if (desktop) {
    const triggerEl = <PopoverTrigger asChild>{trigger}</PopoverTrigger>
    return (
      <Popover open={open} onOpenChange={onOpenChange}>
        {renderField ? <PopoverAnchor asChild>{renderField(triggerEl)}</PopoverAnchor> : triggerEl}
        <PopoverContent align="start" className="w-auto p-3" onOpenAutoFocus={keepFocus} onCloseAutoFocus={backToField}>
          {children}
        </PopoverContent>
      </Popover>
    )
  }
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {renderField ? renderField(<SheetTrigger asChild>{trigger}</SheetTrigger>) : <SheetTrigger asChild>{trigger}</SheetTrigger>}
      <SheetContent
        side="bottom"
        aria-describedby={undefined}
        onOpenAutoFocus={keepFocus}
        onCloseAutoFocus={backToField}
        className="rounded-t-2xl px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3"
      >
        <div aria-hidden className="mx-auto mb-3 h-1 w-10 rounded-full bg-muted-foreground/30" />
        <SheetHeader className="mb-2 text-left">
          <SheetTitle className="text-base">{title}</SheetTitle>
        </SheetHeader>
        <div className="mx-auto w-full max-w-sm">{children}</div>
      </SheetContent>
    </Sheet>
  )
}

const triggerVariants = {
  default:
    'h-9 rounded-md border border-input bg-background px-3 shadow-sm hover:border-ring/60 focus-visible:ring-2 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid=true]:border-destructive',
  ghost: 'h-8 rounded-sm bg-transparent px-1 hover:bg-accent/40 focus-visible:ring-1 disabled:cursor-default disabled:hover:bg-transparent',
} as const

type TriggerProps = Omit<React.ComponentPropsWithoutRef<'button'>, 'value' | 'onChange' | 'type' | 'children'> & {
  /** Visual: `default` = campo de formulário; `ghost` = célula de tabela (atas). */
  variant?: keyof typeof triggerVariants
  placeholder?: string
  /** Mostra "Limpar" (padrão: sim). */
  clearable?: boolean
}

function PickerTrigger({
  ref,
  variant = 'default',
  icon: Icon = CalendarDays,
  display,
  placeholder,
  className,
  disabled,
  ...props
}: Omit<TriggerProps, 'clearable'> & { ref?: React.Ref<HTMLButtonElement>; icon?: LucideIcon; display: string | null }) {
  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled}
      className={cn(
        'flex w-full min-w-0 items-center gap-2 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-ring',
        triggerVariants[variant],
        className,
      )}
      {...props}
    >
      {!(variant === 'ghost' && disabled) && <Icon aria-hidden className="size-4 shrink-0 text-muted-foreground" />}
      {display ? (
        <span className="truncate font-mono tabular-nums">{display}</span>
      ) : (
        <span className="truncate text-muted-foreground">{placeholder}</span>
      )}
    </button>
  )
}

function NavButton({ dir, label, onClick }: { dir: 'prev' | 'next'; label: string; onClick: () => void }) {
  return (
    <Button type="button" variant="ghost" size="icon" className="size-10 sm:size-8" aria-label={label} onClick={onClick}>
      {dir === 'prev' ? <ChevronLeft /> : <ChevronRight />}
    </Button>
  )
}

const fieldVariants = {
  default:
    'h-9 gap-1 rounded-md border border-input bg-background pl-1 pr-3 shadow-sm hover:border-ring/60 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-1 has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50 data-[invalid=true]:border-destructive',
  ghost: 'h-8 gap-0.5 rounded-sm bg-transparent pr-1 hover:bg-accent/40 focus-within:bg-accent/60 focus-within:ring-1 focus-within:ring-ring has-[:disabled]:hover:bg-transparent',
} as const

type FieldProps = Omit<React.ComponentPropsWithoutRef<'input'>, 'value' | 'onChange' | 'type' | 'children' | 'defaultValue'> & {
  /** Visual: `default` = campo de formulário; `ghost` = célula de tabela (atas). */
  variant?: keyof typeof fieldVariants
  placeholder?: string
  /** Aceita apagar o campo e mostra "Limpar" no seletor (padrão: sim). */
  clearable?: boolean
}

/**
 * Campo digitável com o seletor no ícone. Enquanto digita guarda o rascunho;
 * Enter/Tab/sair confirma (o erro aparece logo abaixo, em pt-BR), Esc desfaz,
 * ↑/↓ chamam `onStep`, Alt+↓ abre o seletor.
 */
function TypedField({
  ref,
  value,
  display,
  parse,
  mask,
  onCommit,
  onStep,
  icon: Icon,
  iconLabel,
  title,
  open,
  onOpenChange,
  variant = 'default',
  clearable = true,
  className,
  disabled,
  onBlur,
  onKeyDown,
  onFocus,
  children,
  ...props
}: Omit<FieldProps, 'title'> & {
  ref?: React.Ref<HTMLInputElement>
  value: string
  /** Valor formatado mostrado fora da edição. */
  display: string
  parse: (text: string) => Parsed
  /** Máscara aplicada enquanto digita (`deleting` = apagando). */
  mask?: (text: string, deleting: boolean) => string
  onCommit: (value: string) => void
  /** Setas ↑/↓: devolve o novo valor (`big` = Shift). */
  onStep?: (dir: 1 | -1, big: boolean) => string
  icon: LucideIcon
  iconLabel: string
  title: string
  open: boolean
  onOpenChange: (open: boolean) => void
  children: React.ReactNode
}) {
  const inputRef = React.useRef<HTMLInputElement | null>(null)
  const [draft, setDraft] = React.useState<string | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const errorId = React.useId()

  const setRefs = React.useCallback(
    (el: HTMLInputElement | null) => {
      inputRef.current = el
      if (typeof ref === 'function') ref(el)
      else if (ref) ref.current = el
    },
    [ref],
  )

  // Valor que chega com o campo já focado (hora rápida salva depois do salto de foco):
  // fica selecionado, para a próxima tecla substituir em vez de somar.
  React.useEffect(() => {
    const el = inputRef.current
    if (el && draft === null && document.activeElement === el) el.select()
  }, [display, draft])

  const reset = () => {
    setDraft(null)
    setError(null)
  }

  /** Confirma o rascunho; false se ficou com erro. */
  const commit = (): boolean => {
    if (draft === null) return true
    const parsed = parse(draft)
    if (!parsed.ok) {
      setError(parsed.error)
      return false
    }
    if (!parsed.value && !clearable) {
      setError('Campo obrigatório.')
      return false
    }
    reset()
    if (parsed.value !== value) onCommit(parsed.value)
    return true
  }

  const hideIcon = variant === 'ghost' && disabled

  const field = (trigger: React.ReactNode) => (
    <div
      data-invalid={!!error || undefined}
      className={cn('relative flex w-full min-w-0 items-center text-sm transition-colors', fieldVariants[variant], className)}
    >
      {!hideIcon && trigger}
      <input
        {...props}
        ref={setRefs}
        type="text"
        autoComplete="off"
        spellCheck={false}
        disabled={disabled}
        value={draft ?? display}
        aria-invalid={!!error || props['aria-invalid'] || undefined}
        aria-describedby={error ? errorId : props['aria-describedby']}
        className={cn(
          'h-full w-full min-w-0 bg-transparent font-mono tabular-nums outline-none placeholder:font-sans placeholder:text-muted-foreground disabled:cursor-not-allowed',
          hideIcon ? 'px-1 disabled:cursor-default' : 'pl-0.5',
        )}
        onFocus={(e) => {
          e.currentTarget.select()
          onFocus?.(e)
        }}
        onChange={(e) => {
          const deleting = (e.nativeEvent as InputEvent).inputType?.startsWith('delete') ?? false
          setDraft(mask ? mask(e.target.value, deleting) : e.target.value)
          setError(null)
        }}
        onBlur={(e) => {
          commit()
          onBlur?.(e)
        }}
        onKeyDown={(e) => {
          onKeyDown?.(e)
          if (e.defaultPrevented) return
          if (e.key === 'ArrowDown' && e.altKey) {
            e.preventDefault()
            if (commit()) onOpenChange(true)
          } else if (e.key === 'Enter' && draft !== null) {
            // Só confirma o campo; o próximo Enter segue para o formulário.
            e.preventDefault()
            commit()
          } else if (e.key === 'Escape' && (draft !== null || error)) {
            e.preventDefault()
            e.stopPropagation()
            reset()
          } else if ((e.key === 'ArrowUp' || e.key === 'ArrowDown') && onStep) {
            e.preventDefault()
            const next = onStep(e.key === 'ArrowUp' ? 1 : -1, e.shiftKey)
            reset()
            if (next !== value) onCommit(next)
          }
        }}
      />
      {error && (
        <p
          id={errorId}
          role="alert"
          className="absolute left-0 top-full z-20 mt-1 whitespace-nowrap rounded-sm border border-destructive/40 bg-popover px-1.5 py-0.5 text-xs font-normal text-destructive shadow-sm"
        >
          {error}
        </p>
      )}
    </div>
  )

  return (
    <PickerShell
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      manualFocus
      returnFocus={inputRef}
      renderField={field}
      trigger={
        <button
          type="button"
          tabIndex={-1}
          disabled={disabled}
          aria-label={iconLabel}
          className="flex size-7 shrink-0 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none"
        >
          <Icon aria-hidden className="size-4" />
        </button>
      }
    >
      {children}
    </PickerShell>
  )
}

/** Grade 3×4 de meses de um ano. */
function MonthGrid({
  year,
  selected,
  current,
  min,
  onPick,
}: {
  year: number
  selected: string | null
  current: string
  min?: string
  onPick: (month: string) => void
}) {
  return (
    <div className="grid grid-cols-3 gap-1.5">
      {MONTHS_SHORT.map((label, i) => {
        const month = `${year}-${String(i + 1).padStart(2, '0')}`
        const isSelected = month === selected
        return (
          <button
            key={month}
            type="button"
            disabled={!!min && month < min}
            aria-pressed={isSelected}
            aria-label={`${MONTHS[i]} de ${year}`}
            onClick={() => onPick(month)}
            className={cn(
              'h-11 rounded-md text-sm capitalize transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-35 sm:h-10',
              month === current && !isSelected && 'font-semibold text-primary ring-1 ring-inset ring-primary/50',
              isSelected && 'bg-primary font-semibold text-primary-foreground hover:bg-primary',
            )}
          >
            {label}
          </button>
        )
      })}
    </div>
  )
}

function DateCalendar({
  value,
  today,
  onSelect,
  onClear,
}: {
  value: string
  today: string
  onSelect: (value: string) => void
  onClear?: () => void
}) {
  const [cursor, setCursor] = React.useState(() => (ISO_DATE.test(value) ? value : today))
  const [mode, setMode] = React.useState<'days' | 'months'>('days')
  const gridRef = React.useRef<HTMLDivElement>(null)
  const [y, m] = parts(cursor)

  // Foca o dia do cursor a cada movimento de teclado (ao abrir, quem foca é o PickerShell).
  const focusCursor = React.useRef(false)
  React.useEffect(() => {
    if (!focusCursor.current || mode !== 'days') return
    focusCursor.current = false
    gridRef.current?.querySelector<HTMLButtonElement>(`[data-date="${cursor}"]`)?.focus()
  }, [cursor, mode])

  const first = utc(y, m, 1)
  const start = utc(y, m, 1 - first.getUTCDay())
  const days = Array.from({ length: 42 }, (_, i) => iso(utc(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate() + i)))

  function onKeyDown(e: React.KeyboardEvent) {
    const weekday = utc(y, m, parts(cursor)[2]).getUTCDay()
    const moves: Record<string, () => string> = {
      ArrowLeft: () => addDays(cursor, -1),
      ArrowRight: () => addDays(cursor, 1),
      ArrowUp: () => addDays(cursor, -7),
      ArrowDown: () => addDays(cursor, 7),
      PageUp: () => addMonths(cursor, e.shiftKey ? -12 : -1),
      PageDown: () => addMonths(cursor, e.shiftKey ? 12 : 1),
      Home: () => addDays(cursor, -weekday),
      End: () => addDays(cursor, 6 - weekday),
    }
    const move = moves[e.key]
    if (!move) return
    e.preventDefault()
    focusCursor.current = true
    setCursor(move())
  }

  if (mode === 'months') {
    return (
      <div className="w-full sm:w-[264px]">
        <div className="mb-2 flex items-center justify-between">
          <NavButton dir="prev" label="Ano anterior" onClick={() => setCursor(addMonths(cursor, -12))} />
          <button
            type="button"
            onClick={() => setMode('days')}
            className="rounded-md px-2 py-1 font-mono text-sm font-semibold tabular-nums hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {y}
          </button>
          <NavButton dir="next" label="Próximo ano" onClick={() => setCursor(addMonths(cursor, 12))} />
        </div>
        <MonthGrid
          year={y}
          selected={cursor.slice(0, 7)}
          current={today.slice(0, 7)}
          onPick={(month) => {
            const [py, pm] = month.split('-').map(Number) as [number, number]
            const lastDay = utc(py, pm, 0).getUTCDate()
            focusCursor.current = true
            setCursor(iso(utc(py, pm - 1, Math.min(parts(cursor)[2], lastDay))))
            setMode('days')
          }}
        />
      </div>
    )
  }

  const quick: [string, string][] = [
    ['Hoje', today],
    ['Amanhã', addDays(today, 1)],
    ['Em 7 dias', addDays(today, 7)],
  ]

  return (
    <div className="w-full sm:w-[264px]">
      <div className="mb-1 flex items-center justify-between">
        <NavButton dir="prev" label="Mês anterior" onClick={() => setCursor(addMonths(cursor, -1))} />
        <button
          type="button"
          onClick={() => setMode('months')}
          aria-label={`${MONTHS[m]} de ${y} — escolher mês e ano`}
          className="rounded-md px-2 py-1 text-sm font-semibold hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <span className="capitalize">{MONTHS[m]}</span> <span className="font-mono tabular-nums">{y}</span>
        </button>
        <NavButton dir="next" label="Próximo mês" onClick={() => setCursor(addMonths(cursor, 1))} />
      </div>

      <div ref={gridRef} role="grid" aria-label={`${MONTHS[m]} de ${y}`} onKeyDown={onKeyDown} className="grid grid-cols-7 gap-0.5">
        {WEEKDAYS.map((d, i) => (
          <div key={i} role="columnheader" aria-label={WEEKDAYS_FULL[i]} className="flex h-7 items-center justify-center text-[11px] font-medium text-muted-foreground">
            {d}
          </div>
        ))}
        {days.map((day) => {
          const [dy, dm, dd] = parts(day)
          const selected = day === value
          const isToday = day === today
          const outside = dm !== m
          return (
            <button
              key={day}
              type="button"
              role="gridcell"
              data-date={day}
              tabIndex={day === cursor ? 0 : -1}
              data-picker-focus={day === cursor || undefined}
              aria-selected={selected}
              aria-current={isToday ? 'date' : undefined}
              aria-label={`${dd} de ${MONTHS[dm]} de ${dy}, ${WEEKDAYS_FULL[utc(dy, dm, dd).getUTCDay()]}`}
              onClick={() => onSelect(day)}
              className={cn(
                'flex h-11 items-center justify-center rounded-md font-mono text-sm tabular-nums transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:h-9',
                outside && 'text-muted-foreground/45',
                isToday && !selected && 'font-semibold text-primary ring-1 ring-inset ring-primary/50',
                selected && 'bg-primary font-semibold text-primary-foreground hover:bg-primary',
              )}
            >
              {dd}
            </button>
          )
        })}
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-1 border-t border-border pt-2">
        {quick.map(([label, day]) => (
          <Button key={label} type="button" variant="secondary" size="sm" className="h-9 flex-1 px-2.5 shadow-none sm:h-7 sm:flex-none" onClick={() => onSelect(day)}>
            {label}
          </Button>
        ))}
        {onClear && value && (
          <Button type="button" variant="ghost" size="sm" className="h-9 px-2.5 text-muted-foreground sm:ml-auto sm:h-7" onClick={onClear}>
            Limpar
          </Button>
        )}
      </div>
    </div>
  )
}

export type DatePickerProps = TriggerProps & {
  /** "YYYY-MM-DD" ou '' (vazio). */
  value: string
  onChange: (value: string) => void
  /** Título da folha no mobile (padrão: aria-label ou "Selecionar data"). */
  title?: string
  /** Campo que recebe o foco depois de escolher um dia (ex.: a hora ao lado, já pronta para digitar). */
  thenFocus?: () => HTMLElement | null | undefined
  ref?: React.Ref<HTMLButtonElement>
}

/** Campo de data leve: mostra dd/mm/aaaa e abre o calendário. */
export function DatePicker({ value, onChange, title, thenFocus, clearable = true, placeholder = 'Selecionar data', ...props }: DatePickerProps) {
  const [open, setOpen] = React.useState(false)
  const next = React.useRef<HTMLElement | null>(null)
  const pick = (v: string) => {
    next.current = v ? (thenFocus?.() ?? null) : null
    setOpen(false)
    if (v !== value) onChange(v)
    // Foca já, sem esperar a animação de fechar: a primeira tecla já cai na hora.
    next.current?.focus({ preventScroll: true })
  }
  return (
    <PickerShell
      open={open}
      onOpenChange={(o) => {
        if (o) next.current = null
        setOpen(o)
      }}
      title={title ?? props['aria-label'] ?? 'Selecionar data'}
      manualFocus
      returnFocus={next}
      trigger={<PickerTrigger {...props} placeholder={placeholder} display={ISO_DATE.test(value) ? formatDateValue(value) : null} />}
    >
      <DateCalendar value={value} today={todayInSaoPaulo()} onSelect={pick} onClear={clearable ? () => pick('') : undefined} />
    </PickerShell>
  )
}

export type MonthPickerProps = TriggerProps & {
  /** "YYYY-MM" ou '' (vazio). */
  value: string
  onChange: (value: string) => void
  /** Primeiro mês permitido, "YYYY-MM". */
  min?: string
  title?: string
  ref?: React.Ref<HTMLButtonElement>
}

/** Campo de mês: grade de meses com navegação por ano. */
export function MonthPicker({ value, onChange, min, title, clearable = true, placeholder = 'Selecionar mês', ...props }: MonthPickerProps) {
  const [open, setOpen] = React.useState(false)
  const current = todayInSaoPaulo().slice(0, 7)
  const valid = ISO_MONTH.test(value)
  const [year, setYear] = React.useState(() => Number((valid ? value : current).slice(0, 4)))
  const minYear = min ? Number(min.slice(0, 4)) : -Infinity

  const pick = (v: string) => {
    setOpen(false)
    if (v !== value) onChange(v)
  }
  const display = valid ? `${MONTHS_SHORT[Number(value.slice(5)) - 1]}/${value.slice(0, 4)}` : null

  return (
    <PickerShell
      open={open}
      onOpenChange={(o) => {
        if (o) setYear(Number((valid ? value : current).slice(0, 4)))
        setOpen(o)
      }}
      title={title ?? props['aria-label'] ?? 'Selecionar mês'}
      trigger={<PickerTrigger {...props} placeholder={placeholder} display={display} />}
    >
      <div className="w-full sm:w-[264px]">
        <div className="mb-2 flex items-center justify-between">
          <NavButton dir="prev" label="Ano anterior" onClick={() => setYear((y) => Math.max(minYear, y - 1))} />
          <span className="font-mono text-sm font-semibold tabular-nums">{year}</span>
          <NavButton dir="next" label="Próximo ano" onClick={() => setYear((y) => y + 1)} />
        </div>
        <MonthGrid year={year} selected={valid ? value : null} current={current} min={min} onPick={pick} />
        {clearable && valid && (
          <div className="mt-2 flex justify-end border-t border-border pt-2">
            <Button type="button" variant="ghost" size="sm" className="h-9 px-2.5 text-muted-foreground sm:h-7" onClick={() => pick('')}>
              Limpar
            </Button>
          </div>
        )}
      </div>
    </PickerShell>
  )
}

const ISO_TIME = /^([01]\d|2[0-3]):([0-5]\d)/
const TIME_FORMATTER = new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })

/** Hora atual em São Paulo, "HH:MM". */
function nowInSaoPaulo(): string {
  return TIME_FORMATTER.format(new Date())
}

/** Hora atual arredondada para os próximos 15 min ("09:07" → "09:15"). */
export function nextQuarterHour(): string {
  return roundUpToQuarter(nowInSaoPaulo())
}

/**
 * Hora rápida: ao escolher a data de um campo que tem hora, a hora vazia já vem
 * com a próxima hora cheia de 15 min. Hora já escolhida nunca é trocada.
 */
export function quickTime(date: string, time: string): string {
  return time || !date ? time : nextQuarterHour()
}

const pad = (n: number) => String(n).padStart(2, '0')

/** Grade de horas ou minutos, tudo à vista (sem rolagem). Setas movem na grade, Home/End vão às pontas. */
function TimeGrid({
  label,
  options,
  cols,
  selected,
  initial,
  focusTarget = false,
  onPick,
}: {
  label: string
  options: readonly string[]
  cols: number
  selected: string | null
  /** Item que recebe o foco quando não há seleção. */
  initial?: string
  focusTarget?: boolean
  onPick: (value: string) => void
}) {
  const gridRef = React.useRef<HTMLDivElement>(null)
  const active = selected ?? (initial && options.includes(initial) ? initial : options[0])

  function onKeyDown(e: React.KeyboardEvent) {
    const items = Array.from(gridRef.current?.querySelectorAll<HTMLButtonElement>('[role="option"]') ?? [])
    const i = items.indexOf(document.activeElement as HTMLButtonElement)
    const next = ({ ArrowRight: i + 1, ArrowLeft: i - 1, ArrowDown: i + cols, ArrowUp: i - cols, Home: 0, End: items.length - 1 } as Record<string, number | undefined>)[e.key]
    if (next === undefined) return
    e.preventDefault()
    items[Math.max(0, Math.min(items.length - 1, next))]?.focus()
  }

  return (
    <div className="min-w-0">
      <div className="mb-1 text-[11px] font-medium text-muted-foreground">{label}</div>
      <div
        ref={gridRef}
        role="listbox"
        aria-label={label}
        aria-orientation="horizontal"
        onKeyDown={onKeyDown}
        className="grid gap-0.5"
        style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
      >
        {options.map((option) => {
          const isSelected = option === selected
          return (
            <button
              key={option}
              type="button"
              role="option"
              aria-selected={isSelected}
              tabIndex={option === active ? 0 : -1}
              data-picker-focus={(focusTarget && option === active) || undefined}
              onClick={() => onPick(option)}
              className={cn(
                'flex h-10 items-center justify-center rounded-md font-mono text-sm tabular-nums transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring sm:h-8',
                isSelected && 'bg-primary font-semibold text-primary-foreground hover:bg-primary',
              )}
            >
              {option}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export type TimePickerProps = FieldProps & {
  /** "HH:MM" ou '' (vazio). */
  value: string
  onChange: (value: string) => void
  title?: string
  /** Intervalo da lista de minutos (padrão 5). "Agora" usa o minuto exato. */
  minuteStep?: number
  ref?: React.Ref<HTMLInputElement>
}

/**
 * Campo de hora: só números, os dois-pontos entram sozinhos ("1745" → 17:45,
 * "930" → 09:30, "9" → 09:00). ↑/↓ andam 15 min (Shift 1 h); o ícone abre as
 * grades de hora e minuto (tudo à vista, sem rolar) + "Agora".
 */
export function TimePicker({ value, onChange, title, clearable = true, minuteStep = 5, placeholder = 'hh:mm', ...props }: TimePickerProps) {
  const [open, setOpen] = React.useState(false)
  const match = ISO_TIME.exec(value)
  const hour = match?.[1] ?? null
  const minute = match?.[2] ?? null

  const hours = Array.from({ length: 24 }, (_, i) => pad(i))
  const minutes = Array.from({ length: Math.ceil(60 / minuteStep) }, (_, i) => pad(i * minuteStep))
  if (minute && !minutes.includes(minute)) minutes.push(minute)
  minutes.sort()

  const commit = (v: string, close: boolean) => {
    if (close) setOpen(false)
    if (v !== value) onChange(v)
  }
  const label = title ?? props['aria-label'] ?? 'Selecionar hora'

  return (
    <TypedField
      inputMode="numeric"
      {...props}
      value={value}
      display={match ? `${hour}:${minute}` : ''}
      parse={parseTimeInput}
      mask={maskTimeInput}
      maxLength={5}
      onCommit={onChange}
      onStep={(dir, big) => (match ? stepTime(`${hour}:${minute}`, dir * (big ? 60 : 15)) : nextQuarterHour())}
      clearable={clearable}
      placeholder={placeholder}
      icon={Clock}
      iconLabel={`Abrir seletor de hora: ${label}`}
      title={label}
      open={open}
      onOpenChange={setOpen}
    >
      <div className="w-full space-y-3 sm:w-[264px]">
        <TimeGrid label="Hora" options={hours} cols={6} selected={hour} initial={nowInSaoPaulo().slice(0, 2)} focusTarget onPick={(h) => commit(`${h}:${minute ?? '00'}`, false)} />
        <TimeGrid label="Minuto" options={minutes} cols={6} selected={minute} onPick={(m) => commit(`${hour ?? nowInSaoPaulo().slice(0, 2)}:${m}`, true)} />
        <div className="mt-2 flex items-center gap-1 border-t border-border pt-2">
          <Button type="button" variant="secondary" size="sm" className="h-9 flex-1 px-2.5 shadow-none sm:h-7" onClick={() => commit(nowInSaoPaulo(), true)}>
            <Clock /> Agora
          </Button>
          {clearable && match && (
            <Button type="button" variant="ghost" size="sm" className="h-9 px-2.5 text-muted-foreground sm:h-7" onClick={() => commit('', true)}>
              Limpar
            </Button>
          )}
        </div>
      </div>
    </TypedField>
  )
}
